import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/series-label';
import HighchartsReact from 'highcharts-react-official';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MappedAssetSensor } from '@/common/asset-sensor';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/context/ThemeContext';
import { HIGH_CHART_DARK_THEME, HIGH_CHART_LIGHT_THEME } from '@/lib/highchart/setup';
import {
	type MetSensorTimeSeriesChartFilters,
	useQueryGetMetTimeSeriesChart,
} from '@/queries/assets/sensors/met/get-timeseries-chart';
import { AbsoluteLoader } from '../../../absoluteLoader';
import { EmptyChart } from '../../EmptyChart';

function baseChartOptions(titleText: string, yAxisTitle: string): Highcharts.Options {
	return {
		chart: {
			zooming: {
				type: 'x',
			},
		},
		title: { text: titleText, style: { fontSize: '14px' } },
		xAxis: { type: 'datetime' },
		yAxis: [{ title: { text: yAxisTitle } }],
		tooltip: { shared: true },
	};
}

export type MeteoTimeSeriesChartsFilters = {
	startDate: Date | undefined;
	endDate: Date | undefined;
	aggregation: MetSensorTimeSeriesChartFilters['aggregation'] | 'none';
};

export type MeteoTimeSeriesChartsProps = {
	selectedSensor: MappedAssetSensor;
	assetId: string;
	filters: MeteoTimeSeriesChartsFilters;
};

export function MeteoTimeSeriesCharts({ selectedSensor, assetId, filters }: MeteoTimeSeriesChartsProps) {
	const { t } = useTranslation();
	const { isDarkTheme } = useTheme();
	const meteoChartFilters = useMemo(
		() => ({
			from: filters.startDate?.toISOString(),
			to: filters.endDate?.toISOString(),
			aggregation: filters.aggregation !== 'none' ? filters.aggregation : undefined,
			getAllFields: true,
		}),
		[filters.startDate, filters.endDate, filters.aggregation],
	);
	const { data: meteoChartResponse, isLoading } = useQuery(
		useQueryGetMetTimeSeriesChart(assetId, selectedSensor.id, meteoChartFilters),
	);
	const chartData = meteoChartResponse?.chartData || [];
	const chartsFinalData = useMemo(() => {
		const tempChartData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.t] as [number, number],
		);
		const pressureChartData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.p] as [number, number],
		);
		const humidityChartData = chartData.map(
			(record) => [new Date(record.measGroup).getTime(), record.h] as [number, number],
		);

		const temperatureLabel = t('health.charts.temperatureUnit');
		const pressureLabel = t('health.charts.pressure');
		const humidityLabel = t('health.charts.humidity');
		return [
			{
				key: 'temp',
				baseOptions: baseChartOptions(temperatureLabel, temperatureLabel),
				series: [{ name: temperatureLabel, data: tempChartData, type: 'line', zIndex: 5 }],
			},
			{
				key: 'pressure',
				baseOptions: baseChartOptions(pressureLabel, pressureLabel),
				series: [{ name: pressureLabel, data: pressureChartData, type: 'line', zIndex: 5 }],
			},
			{
				key: 'humidity',
				baseOptions: baseChartOptions(humidityLabel, humidityLabel),
				series: [{ name: humidityLabel, data: humidityChartData, type: 'line', zIndex: 5 }],
			},
		];
	}, [chartData, t]);
	const charts = useMemo(
		() =>
			chartsFinalData.map((data) => ({
				...data,
				baseOptions: merge(
					structuredClone(isDarkTheme ? HIGH_CHART_DARK_THEME : HIGH_CHART_LIGHT_THEME),
					data.baseOptions,
				),
			})),
		[chartsFinalData, isDarkTheme],
	);
	if (isLoading) {
		return (
			<div className="h-50 relative w-full">
				<AbsoluteLoader className="bg-transparent" isLoading />
			</div>
		);
	}
	if (!chartData?.length) {
		return <EmptyChart title={t('health.charts.noDataTitle')} subtitle={t('health.charts.noDataSubtitle')} />;
	}
	return (
		<div className="space-y-3">
			{charts.map((chart) => (
				<Card key={chart.key} className="py-0">
					<CardContent>
						<HighchartsReact
							highcharts={Highcharts}
							options={{
								...chart.baseOptions,
								series: chart.series,
							}}
						/>
					</CardContent>
				</Card>
			))}
		</div>
	);
}
